// src/components/dashboard/HoursBalanceSummaryBar.js

import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDashboard } from '../../contexts/DashboardContext';
import '../../styles/dashboard.css';

/**
 * "Stundensaldo-Übersicht" — company-wide hours-account balance summary bar.
 * Full-width card above the tab panel: title, total staff-with-accounts count,
 * a 3-segment stacked bar (positive/balanced/negative), and a legend.
 *
 * Data: useDashboard().hoursBalanceOutliers.counts (added in
 * DashboardService.getHoursBalanceOutliers()). Fetched the same on-demand way
 * HoursBalanceOutliersWidget does — calls loadHoursBalanceOutliers() on mount.
 * Owner-gating is the parent's (CompanyDashboard.js) responsibility, same
 * convention as every other dashboard widget — this component does not
 * self-check userRole.
 */
const HoursBalanceSummaryBar = () => {
  const { t } = useTranslation('dashboard');

  const {
    hoursBalanceOutliers,
    hoursBalanceOutliersLoading,
    hoursBalanceOutliersError,
    loadHoursBalanceOutliers
  } = useDashboard();

  // Fetch on mount — same call/dependency pattern as HoursBalanceOutliersWidget.
  // Note: both this bar (always visible for owners) and that widget (only
  // visible when its tab is active) independently call loadHoursBalanceOutliers()
  // on their own mount. There's no request de-duplication in DashboardContext,
  // so if both are mounted at once the second call simply overwrites the same
  // hoursBalanceOutliers state with an equivalent result — harmless, but
  // flagging as an accepted minor double-fetch rather than a silent surprise.
  useEffect(() => {
    loadHoursBalanceOutliers();
  }, [loadHoursBalanceOutliers]);

  const counts = hoursBalanceOutliers?.counts || {
    positive: 0,
    balanced: 0,
    negative: 0,
    total: 0
  };
  const { positive, balanced, negative, total } = counts;

  // Bar-fill baseline fix: some staff may lack an hoursAccounts doc, so
  // positive + balanced + negative can be LESS than counts.total (known
  // DashboardService edge case — see getHoursBalanceOutliers()). Using
  // counts.total as the percentage denominator left a visible unfilled
  // gap at the end of the track whenever that mismatch occurred. The bar
  // itself should always visually close edge-to-edge, so its three
  // segment widths are computed against the categorized sum instead.
  // The header total / legend counts below still use the real `total`
  // and per-bucket counts from `counts` — only the bar's proportions
  // use this separate baseline.
  const categorizedTotal = positive + balanced + negative;

  const positivePct = categorizedTotal > 0 ? (positive / categorizedTotal) * 100 : 0;
  const balancedPct = categorizedTotal > 0 ? (balanced / categorizedTotal) * 100 : 0;
  const negativePct = categorizedTotal > 0 ? (negative / categorizedTotal) * 100 : 0;

  // Same "loading before first successful fetch only" convention as
  // HoursBalanceOutliersWidget — a manual refresh won't blank the bar.
  const isLoading = hoursBalanceOutliersLoading && !hoursBalanceOutliers;

  return (
    <div className="hours-balance-summary-bar">
      <div className="hours-balance-summary-header">
        <h3>{t('hoursSummary.title')}</h3>
        <span className="hours-balance-summary-total">
          {isLoading ? '…' : total} {t('hoursSummary.employees')}
        </span>
      </div>

      {hoursBalanceOutliersError ? (
        <p className="error-message">× {hoursBalanceOutliersError}</p>
      ) : (
        <>
          <div className="hours-balance-summary-track">
            <div
              className="hours-balance-summary-segment hours-balance-summary-segment--positive"
              style={{ width: `${isLoading ? 0 : positivePct}%` }}
            />
            <div
              className="hours-balance-summary-segment hours-balance-summary-segment--balanced"
              style={{ width: `${isLoading ? 0 : balancedPct}%` }}
            />
            <div
              className="hours-balance-summary-segment hours-balance-summary-segment--negative"
              style={{ width: `${isLoading ? 0 : negativePct}%` }}
            />
          </div>

          <div className="hours-balance-summary-legend">
            <span className="legend-item">
              <span className="legend-dot legend-dot--positive" />
              {t('hoursSummary.positive')} · {isLoading ? '…' : positive}
            </span>
            <span className="legend-item">
              <span className="legend-dot legend-dot--balanced" />
              {t('hoursSummary.balanced')} · {isLoading ? '…' : balanced}
            </span>
            <span className="legend-item">
              <span className="legend-dot legend-dot--negative" />
              {t('hoursSummary.negative')} · {isLoading ? '…' : negative}
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default HoursBalanceSummaryBar;